import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { LogIn, UserPlus, AlertCircle, Lock } from "lucide-react";

export default function AuthModal({ open, onOpenChange, onSuccess }) {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("register");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const result = mode === "login"
      ? await login(email, password)
      : await register(email, password, name, pin);
    if (result.success) {
      onSuccess?.();
      onOpenChange(false);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-['Playfair_Display'] flex items-center gap-2">
            <Lock className="w-5 h-5 text-[#1A362D]" />
            {mode === "login" ? "Sign In" : "Create Your Account"}
          </DialogTitle>
          <DialogDescription>
            {mode === "login"
              ? "Sign in to access your saved analyses and uploads."
              : "Create an account to upload your own files and save your work."}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="flex items-center gap-2 p-3 bg-[#E07A5F]/10 border border-[#E07A5F]/20 rounded-md text-sm text-[#E07A5F]" data-testid="auth-modal-error">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-3">
          {mode === "register" && (
            <div>
              <Label>Full Name</Label>
              <Input
                data-testid="modal-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
              />
            </div>
          )}
          <div>
            <Label>Email</Label>
            <Input
              data-testid="modal-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              required
            />
          </div>
          <div>
            <Label>Password</Label>
            <Input
              data-testid="modal-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              required
              minLength={4}
            />
          </div>
          {mode === "register" && (
            <div>
              <Label>4-Digit Security PIN</Label>
              <Input
                data-testid="modal-pin-input"
                type="text"
                inputMode="numeric"
                maxLength={4}
                pattern="[0-9]*"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                placeholder="e.g. 1234"
                required
              />
              <p className="text-xs text-[#78716C] mt-1">Required each time you log in</p>
            </div>
          )}
          <Button
            data-testid="modal-auth-submit"
            type="submit"
            disabled={loading}
            className="w-full bg-[#1A362D] hover:bg-[#264B40] h-10"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : mode === "login" ? (
              <><LogIn className="w-4 h-4 mr-2" /> Sign In</>
            ) : (
              <><UserPlus className="w-4 h-4 mr-2" /> Create Account</>
            )}
          </Button>
        </form>

        <div className="text-center">
          <button
            data-testid="modal-toggle-mode"
            onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
            className="text-sm font-medium text-[#1A362D] hover:underline"
          >
            {mode === "login" ? "Need an account? Create one" : "Already have an account? Sign in"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
